import {
  durationString,
  monthDateString,
  TimeOffEvent,
  timeStringFrom24HourTimeString,
} from '@nextforce/api'
import { Divider, makeStyles, Text } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { t } from 'app/i18l'
import moment from 'moment'
import { Pressable, View } from 'react-native'
import { RoundedButton } from 'shared/components/rounded-button'

interface Props {
  events: TimeOffEvent[]
  onPress(timeOffEvent: TimeOffEvent): void
}

const HELPER_TIME_OFF_MARGIN = 3

export function HelperTimeOffEventView({ events, onPress }: Props) {
  const styles = useStyles()

  const approvedEvents = events.filter(
    timeOffEvent => timeOffEvent.status === 'APPROVED'
  )
  const rejectedEvents = events.filter(
    timeOffEvent => timeOffEvent.status === 'REJECTED'
  )
  const pendingEvents = events.filter(
    timeOffEvent => timeOffEvent.status === 'PENDING'
  )
  return (
    <Pressable
      style={{
        flex: 1,
      }}
    >
      {pendingEvents.length !== 0 ? (
        <View style={styles.card}>{getHelperTimeOffCards(pendingEvents)}</View>
      ) : (
        <></>
      )}
      {approvedEvents.length !== 0 ? (
        <View style={styles.card}>{getHelperTimeOffCards(approvedEvents)}</View>
      ) : (
        <></>
      )}
      {rejectedEvents.length !== 0 ? (
        <View style={styles.card}>{getHelperTimeOffCards(rejectedEvents)}</View>
      ) : (
        <></>
      )}
    </Pressable>
  )

  function getHelperTimeOffCards(timeOffEvents: TimeOffEvent[]) {
    return timeOffEvents.map(getHelperTimeOffCard)
  }

  function getHelperTimeOffCard(timeOffEvent: TimeOffEvent) {
    const isPastDate = moment(timeOffEvent.startDate).isBefore(moment())
    const statusText =
      timeOffEvent.status === 'APPROVED'
        ? t('approvedTimeOff')
        : timeOffEvent.status === 'REJECTED'
        ? t('rejectedTimeOff')
        : t('requestedTimeOff')
    return (
      <View
        key={timeOffEvent.id}
        style={{
          marginHorizontal: 20,
          height: HELPER_TIME_OFF_CARD_HEIGHT,
          justifyContent: 'center',
        }}
      >
        <View style={styles.cardTitleContainer}>
          <Text>
            {timeOffEvent.allDay
              ? t('allDay')
              : timeStringFrom24HourTimeString(timeOffEvent.startTime)}
          </Text>
          {timeOffEvent.status === 'PENDING' && !isPastDate ? (
            <RoundedButton
              onPress={() => onPress(timeOffEvent)}
              title={t('makeChanges')}
              disabled={!onlineManager.isOnline()}
            />
          ) : null}
        </View>

        <Divider style={styles.text} />
        <Text style={[styles.text]}>{statusText}</Text>
        {timeOffEvent.startDate === timeOffEvent.endDate ? (
          <Text style={styles.text}>{`${monthDateString(
            timeOffEvent.startDate
          )}`}</Text>
        ) : (
          <Text style={styles.text}>{`${monthDateString(
            timeOffEvent.startDate
          )} - ${monthDateString(timeOffEvent.endDate)}`}</Text>
        )}
        {timeOffEvent.allDay ? (
          <></>
        ) : (
          <Text style={styles.text}>
            {`${durationString(
              timeOffEvent.duration
            )} (${timeStringFrom24HourTimeString(
              timeOffEvent.startTime
            )} - ${timeStringFrom24HourTimeString(timeOffEvent.endTime)})`}
          </Text>
        )}
      </View>
    )
  }
}

export const HELPER_TIME_OFF_CARD_HEIGHT = 130

export function getHelperTimeOffEventsHeight(timeOffEvents: TimeOffEvent[]) {
  let height = timeOffEvents.length * HELPER_TIME_OFF_CARD_HEIGHT

  if (timeOffEvents.some(timeOffEvent => timeOffEvent.status === 'APPROVED')) {
    height += HELPER_TIME_OFF_MARGIN * 2
  }
  if (timeOffEvents.some(timeOffEvent => timeOffEvent.status === 'REJECTED')) {
    height += HELPER_TIME_OFF_MARGIN * 2
  }
  return height
}

const useStyles = makeStyles(theme => ({
  card: {
    flex: 1,
    backgroundColor: theme.colors.grey4,
    justifyContent: 'center',
    borderRadius: 10,
    paddingVertical: 5,
    marginVertical: HELPER_TIME_OFF_MARGIN,
  },
  text: {
    fontWeight: 'normal',
    marginBottom: 10,
    textAlignVertical: 'center',
  },
  cardTitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}))
